import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Alert,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { 
  DATA_SOURCES, 
  SOURCE_ENDPOINTS, 
  FILTER_DEFINITIONS, 
  FILTER_VALUE_OPTIONS, 
  ENUMS 
} from '../JobsList/dataSourceConstants';

// Import sub-components
import { BasicInfoSection } from './BasicInfoSection';
import { DataSourceSection } from './DataSourceSection';
import { TimeRangeSection } from './TimeRangeSection';
import { ScheduleSection } from './ScheduleSection';
import { AdvancedSettingsSection } from './AdvancedSettingsSection';

// Import form validation schema
import { createValidationSchema } from './JobFormValidation';

// Helper functions (extracted to improve maintainability)
import { 
  getEndpointsForDataSource,
  getFiltersForEndpoint,
  getOptionsForFilter,
  hasError,
  getErrorMessage,
  hasNestedError, 
  getNestedErrorMessage
} from './JobFormHelpers';

export const JobForm = forwardRef(({ defaultValues, onSubmit, error, loading, ...props }, ref) => {
  console.log('JobForm - defaultValues', defaultValues);
  // State to track expanded accordion sections
  const [expanded, setExpanded] = useState({
    basic: true,
    source: true,
    timeRange: true,
    schedule: true,
    advanced: false
  });

  // State to track available endpoints based on selected data source
  const [availableEndpoints, setAvailableEndpoints] = useState([]);
  
  // State to track available filters based on selected endpoint
  const [availableFilters, setAvailableFilters] = useState([]);

  // Form validation schema
  const validationSchema = createValidationSchema();

  // Initialize Formik
  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema,
    onSubmit: (values) => {
      console.log('JobForm onSubmit', values)
      onSubmit(values);
    }
  });

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      console.log('submitForm called with values:', formik.values);
      console.log('Current formik errors:', formik.errors);
      console.log('Is form valid?', formik.isValid);
      console.log('Has form been touched?', Object.keys(formik.touched).length > 0);
      
      formik.handleSubmit();
    }
  }));

  // Update available endpoints when data source changes
  useEffect(() => {
    if (formik.values.dataSource) {
      const endpoints = getEndpointsForDataSource(formik.values.dataSource);
      setAvailableEndpoints(endpoints);
      
      // If the current endpoint is not valid for the new data source, reset it
      const isCurrentEndpointValid = endpoints.some(
        endpoint => endpoint.endpointId === formik.values.sourceEndpoint
      );
      
      if (!isCurrentEndpointValid && endpoints.length > 0) {
        formik.setFieldValue('sourceEndpoint', endpoints[0].endpointId);
      }
    }
  }, [formik.values.dataSource]);

  // Update available filters when endpoint changes
  useEffect(() => {
    if (formik.values.sourceEndpoint) {
      const filters = getFiltersForEndpoint(formik.values.sourceEndpoint);
      setAvailableFilters(filters);
      
      // Initialize sourceFilters if empty or reset if endpoint changed
      if (!formik.values.sourceFilters || formik.values.sourceFilters.length === 0) {
        const initialFilters = filters.map(filter => ({
          name: filter.name,
          values: filter.isRequired ? ['*'] : [] // Default to "all" for required filters
        }));
        formik.setFieldValue('sourceFilters', initialFilters);
      }
    }
  }, [formik.values.sourceEndpoint]);

  // Handle accordion expansion
  const handleAccordionChange = (section) => (event, isExpanded) => {
    setExpanded({ ...expanded, [section]: isExpanded });
  };

  // Helper props to pass to all section components
  const sectionProps = {
    formik,
    loading,
    hasError: (field) => hasError(formik, field),
    getErrorMessage: (field) => getErrorMessage(formik, field),
    hasNestedError: (parent, field) => hasNestedError(formik, parent, field),
    getNestedErrorMessage: (parent, field) => getNestedErrorMessage(formik, parent, field)
  };

  return (
    <div>
      <form id="job-definition-form" onSubmit={formik.handleSubmit}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        
        {/* Basic Information */}
        <Accordion
          expanded={expanded.basic}
          onChange={handleAccordionChange('basic')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              Basic Information
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <BasicInfoSection 
              {...sectionProps}
              statusOptions={ENUMS.JobDefinitionStatus}
            />
          </AccordionDetails>
        </Accordion>
        
        {/* Data Source Configuration */}
        <Accordion
          expanded={expanded.source}
          onChange={handleAccordionChange('source')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              Data Source Configuration
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DataSourceSection 
              {...sectionProps}
              dataSources={DATA_SOURCES}
              availableEndpoints={availableEndpoints}
              availableFilters={availableFilters}
              getOptionsForFilter={getOptionsForFilter}
            />
          </AccordionDetails>
        </Accordion>
        
        {/* Data Time Range Configuration */}
        <Accordion
          expanded={expanded.timeRange}
          onChange={handleAccordionChange('timeRange')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              Data Time Range Configuration
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TimeRangeSection 
              {...sectionProps}
              timeRangeModes={ENUMS.DataTimeMode}
              timeUnits={ENUMS.TimeUnit}
              timeGranularities={ENUMS.TimeGranularity}
            />
          </AccordionDetails>
        </Accordion>
        
        {/* Schedule Configuration */}
        <Accordion
          expanded={expanded.schedule}
          onChange={handleAccordionChange('schedule')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              Schedule Configuration
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ScheduleSection 
              {...sectionProps}
              scheduleModes={ENUMS.ScheduleMode}
              timeZoneStrategies={ENUMS.TimeZoneStrategy}
            />
          </AccordionDetails>
        </Accordion>
        
        {/* Advanced Settings */}
        <Accordion
          expanded={expanded.advanced}
          onChange={handleAccordionChange('advanced')}
          sx={{ mb: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">
              Advanced Settings
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <AdvancedSettingsSection {...sectionProps} />
          </AccordionDetails>
        </Accordion>
      </form>
    </div>
  );
});